/**
 * Indicador visual central da sessão de voz.
 *
 * - conectando / reconectando / pedindo microfone → spinner discreto
 * - ouvindo → círculo com pulso no primary (Byzantine blue)
 * - falando → barras animadas (equalizador)
 * - executando ferramenta → spinner no accent
 * - aguardando confirmação → escudo no primary (a trava de escritas)
 * - encerrando / encerrada / erro → círculo estático muted
 *
 * CSS/Tailwind puro com os tokens do design system — sem libs novas.
 */
import { Loader, Mic, ShieldAlert } from 'lucide-react'

import type { VoiceSessionStatus } from '@/hooks/useVoiceSession'
import { cn } from '@/lib/utils'

const BAR_DELAYS = ['-0.45s', '-0.3s', '-0.15s', '-0.3s', '-0.45s'] as const
const BAR_HEIGHTS = ['h-4', 'h-7', 'h-10', 'h-7', 'h-4'] as const

export function VoiceIndicator({ status }: { status: VoiceSessionStatus }) {
  if (status === 'speaking') {
    return (
      <div
        className="flex size-24 items-center justify-center gap-1.5"
        role="status"
        aria-label="O agente está falando"
      >
        {BAR_DELAYS.map((delay, i) => (
          <span
            key={`${delay}-${BAR_HEIGHTS[i]}`}
            className={cn('bg-primary w-1.5 animate-pulse rounded-full', BAR_HEIGHTS[i])}
            style={{ animationDelay: delay, animationDuration: '0.9s' }}
          />
        ))}
      </div>
    )
  }

  if (status === 'executing-tool') {
    return (
      <div
        className="border-border bg-card flex size-24 items-center justify-center rounded-full border"
        role="status"
        aria-label="Executando ferramenta"
      >
        <Loader className="text-primary size-8 animate-spin" />
      </div>
    )
  }

  if (status === 'awaiting-confirmation') {
    return (
      <div
        className="border-primary/40 bg-card flex size-24 items-center justify-center rounded-full border"
        role="status"
        aria-label="Aguardando confirmação"
      >
        <ShieldAlert className="text-primary size-8" />
      </div>
    )
  }

  if (status === 'requesting-mic' || status === 'connecting' || status === 'reconnecting') {
    return (
      <div
        className="border-border bg-card flex size-24 items-center justify-center rounded-full border"
        role="status"
        aria-label={status === 'reconnecting' ? 'Reconectando' : 'Conectando'}
      >
        <Loader className="text-muted-foreground size-8 animate-spin" />
      </div>
    )
  }

  if (status === 'listening') {
    return (
      <div
        className="relative flex size-24 items-center justify-center"
        role="status"
        aria-label="Ouvindo"
      >
        <span className="bg-primary/25 absolute inset-0 animate-ping rounded-full [animation-duration:1.6s]" />
        <span className="bg-primary/15 absolute inset-2 rounded-full" />
        <span className="bg-primary relative flex size-16 items-center justify-center rounded-full shadow-sm">
          <Mic className="text-primary-foreground size-7" />
        </span>
      </div>
    )
  }

  // idle | ending | ended | error → estado estático e silencioso.
  return (
    <div className="border-border bg-muted flex size-24 items-center justify-center rounded-full border">
      <Mic className="text-muted-foreground size-7" />
    </div>
  )
}
